'use client'

import { cn } from '@/lib/utils'
import { motion, useReducedMotion } from 'framer-motion'
import { CheckCheck } from 'lucide-react'
import type { ReactNode } from 'react'

export interface Message {
  id: string
  text: ReactNode
  sender: 'user' | 'agent'
  time?: string
  isTyping?: boolean
}

interface ChatMockupProps {
  messages: Message[]
  className?: string
  agentName?: string
  agentAvatar?: string
}

export function ChatMockup({
  messages,
  className,
  agentName = 'Asistente IA',
  agentAvatar,
}: ChatMockupProps) {
  const shouldReduceMotion = useReducedMotion()

  return (
    <div
      className={cn(
        'w-full max-w-md overflow-hidden rounded-[32px] border border-black/5 bg-[#F0F2F5] font-sans shadow-2xl dark:border-white/5 dark:bg-[#0B141A]',
        className
      )}
    >
      {/* Header */}
      <div className="relative z-10 flex items-center gap-3 bg-[#008069] px-4 py-3 shadow-sm">
        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-white/20 font-bold text-white">
          {agentAvatar ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={agentAvatar}
              alt={agentName}
              className="h-full w-full object-cover"
              width={40}
              height={40}
              loading="lazy"
              decoding="async"
            />
          ) : (
            'AI'
          )}
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold leading-tight text-white">{agentName}</h3>
          <p className="text-xs text-white/80">en línea</p>
        </div>
      </div>

      {/* Chat Area */}
      <div className="max-h-[400px] min-h-[300px] space-y-4 overflow-y-auto bg-[url('/chat-bg.svg')] bg-[length:120px_120px] bg-repeat p-4 dark:opacity-90">
        {messages.map((msg, i) => {
          const content = (
            <div
              className={cn(
                'relative max-w-[85%] rounded-lg px-3 py-1.5 text-[15px] leading-relaxed shadow-sm',
                msg.sender === 'user'
                  ? 'rounded-tr-none bg-[#D9FDD3] text-black dark:bg-[#005C4B] dark:text-white'
                  : 'rounded-tl-none bg-white text-black dark:bg-[#202C33] dark:text-white'
              )}
            >
              {msg.isTyping ? (
                <div className="flex h-6 items-center gap-1 px-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-400 [animation-delay:-0.3s] motion-safe:animate-bounce"></span>
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-400 [animation-delay:-0.15s] motion-safe:animate-bounce"></span>
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-400 motion-safe:animate-bounce"></span>
                </div>
              ) : (
                <>
                  <p className="whitespace-pre-wrap break-words [&_a]:text-[#008069] [&_a]:underline [&_a]:underline-offset-2 dark:[&_a]:text-emerald-300 [&_strong]:font-semibold">
                    {msg.text}
                  </p>
                  <div
                    className={cn(
                      'mt-1 flex items-center justify-end gap-1',
                      'text-[10px] opacity-60'
                    )}
                  >
                    <span>{msg.time || '12:00'}</span>
                    {msg.sender === 'user' && (
                      <CheckCheck className="h-3 w-3 text-blue-500" aria-hidden="true" />
                    )}
                  </div>
                </>
              )}

              {/* Message Tail SVG */}
              <svg
                viewBox="0 0 8 13"
                className={cn(
                  'absolute top-0 h-3 w-2 fill-current drop-shadow-sm',
                  msg.sender === 'user'
                    ? '-right-2 text-[#D9FDD3] dark:text-[#005C4B]'
                    : '-left-2 scale-x-[-1] transform text-white dark:text-[#202C33]'
                )}
                aria-hidden="true"
              >
                <path d="M5.188 1H0v11.193l6.467-8.625C7.526 2.156 6.958 1 5.188 1z" />
              </svg>
            </div>
          )

          if (shouldReduceMotion) {
            return (
              <div
                key={msg.id}
                className={cn(
                  'flex w-full',
                  msg.sender === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {content}
              </div>
            )
          }

          return (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.3, delay: i * 0.5 }}
              className={cn('flex w-full', msg.sender === 'user' ? 'justify-end' : 'justify-start')}
            >
              {content}
            </motion.div>
          )
        })}
      </div>

      {/* Input Area (Fake) */}
      <div className="flex items-center gap-2 border-t border-black/5 bg-[#F0F2F5] px-2 py-2 dark:border-white/5 dark:bg-[#202C33]">
        <div className="flex h-8 w-8 items-center justify-center text-gray-500 dark:text-gray-400">
          <span className="text-xl">+</span>
        </div>
        <div className="flex h-9 flex-1 items-center rounded-lg bg-white px-4 text-sm text-gray-400 dark:bg-[#2A3942]">
          Escribe un mensaje
        </div>
        <div className="flex h-10 w-10 items-center justify-center text-[#008069]">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#008069] text-xs text-white">
            🎤
          </div>
        </div>
      </div>
    </div>
  )
}
